import { AfterViewInit,
  ComponentRef,
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  Input,
  NgZone,
  OnDestroy,
  Renderer2,
  TemplateRef,
  ViewContainerRef } from '@angular/core';
import { MpContextualMenuComponent } from './mp-contextual-menu.component';
import { DomEventListenerService } from '../../services/dom-event-listener.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Directive({
  selector: '[mpContextualMenu]',
  standalone: true
})
export class MpContextualMenuDirective implements OnDestroy, AfterViewInit {

  @Input() public mpContextualMenu: TemplateRef<HTMLElement>;
  @Input() public mpContextualMenuData: unknown;
  @Input() public mpContextualMenuDisabled: unknown;
  @Input() public mpContextualOpenOnHoverMode: boolean = false;

  protected static componentRef: ComponentRef<MpContextualMenuComponent>;
  protected static componentRefAnchorElementRef: ElementRef<HTMLElement>;

  private _destroyRef = inject(DestroyRef);

  constructor(private readonly elementRef: ElementRef,
              private readonly domListener: DomEventListenerService,
              private readonly viewContainerRef: ViewContainerRef,
              private readonly renderer2: Renderer2,
              private readonly ngZone: NgZone) {
  }

  private clickHandler() {
    if (this.mpContextualMenuDisabled) {
      return;
    }

    this.renderer2.listen(this.elementRef.nativeElement, 'click', (event) => {
      this.ngZone.runOutsideAngular(() => {
        if (MpContextualMenuDirective.componentRef && MpContextualMenuDirective.componentRefAnchorElementRef !== this.elementRef) {
          this.removeContextual();
        }

        if (!MpContextualMenuDirective.componentRef && this.elementRef.nativeElement.contains(event.target)) {
          this.ngZone.run(() => {
            this.subscribeToDomEvents();
            this.openContextualMenu();
          });
        }

        if (MpContextualMenuDirective.componentRef && this.elementRef.nativeElement.contains(event.target)) {
          this.removeContextual();
        }
      });
    });
  }

  private hoverHandler() {
    if (this.mpContextualMenuDisabled) {
      return;
    }

    this.renderer2.listen(this.elementRef.nativeElement, 'mouseenter', (event) => {
      this.ngZone.runOutsideAngular(() => {
        if (!MpContextualMenuDirective.componentRef && this.elementRef.nativeElement.contains(event.target)) {
          this.ngZone.run(() => this.openContextualMenu());
        }
      });
    });

    this.renderer2.listen(this.elementRef.nativeElement, 'mouseleave', () => {
      this.ngZone.runOutsideAngular(() => {
        if (MpContextualMenuDirective.componentRef) {
          this.ngZone.run(() => this.removeContextual());
        }
      });
    });
  }

  ngAfterViewInit() {
    if (this.mpContextualOpenOnHoverMode) {
      this.hoverHandler();
    } else {
      this.clickHandler();
    }
  }

  ngOnDestroy(): void {
    this.removeContextual();
  }

  public removeContextual(): void {
    MpContextualMenuDirective.componentRef?.destroy();
    MpContextualMenuDirective.componentRef = undefined;
    MpContextualMenuDirective.componentRefAnchorElementRef = undefined;
  }

  private onDocumentScroll(): void {
    this.removeContextual();
  }

  private onDocumentClick() {
    this.removeContextual();
  }

  private openContextualMenu() {
    requestAnimationFrame(() => {
      MpContextualMenuDirective.componentRef = this.viewContainerRef.createComponent(MpContextualMenuComponent);
      MpContextualMenuDirective.componentRef.setInput('mpContextualMenu', this.mpContextualMenu);
      MpContextualMenuDirective.componentRef.setInput('mpContextualMenuData', this.mpContextualMenuData);
      MpContextualMenuDirective.componentRef.setInput('anchor', this.elementRef);
      this.renderer2.appendChild(document.body, MpContextualMenuDirective.componentRef.location.nativeElement);
      MpContextualMenuDirective.componentRefAnchorElementRef = this.elementRef;
    });
  }

  private subscribeToDomEvents(): void {
    this.ngZone.runOutsideAngular(() => {
      this.domListener.onScrollObservable.pipe(takeUntilDestroyed(this._destroyRef)).subscribe({ next: (wEvent: WheelEvent) => this.onDocumentScroll() });
      this.domListener.onClickObservable.pipe(takeUntilDestroyed(this._destroyRef)).subscribe({ next: (mEvent: MouseEvent) => this.onDocumentClick() });
    });
  }
}
